import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User } from 'firebase/auth'; // Use type import for User
// Firebase imports will be done dynamically

type AuthContextType = {
  user: User | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    // Dynamically import auth and set up listener
    import('../lib/firebase').then(({ auth }) => {
      unsubscribe = auth.onAuthStateChanged((user) => {
        setUser(user);
        setIsLoading(false);
        setError(null); // Clear error on auth state change
      });
    }).catch(err => {
      console.error("Failed to load Firebase auth listener:", err);
      setError("Could not initialize authentication.");
      setIsLoading(false);
    });

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const signInWithGoogle = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Dynamically import necessary functions and auth object
      const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
      const { auth } = await import('../lib/firebase');

      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // User state will be updated by the onAuthStateChanged listener
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      const { auth } = await import('../lib/firebase');
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      console.error('Error signing in with email:', error);
      setError(error.message || 'Failed to sign in with email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { createUserWithEmailAndPassword } = await import('firebase/auth');
      const { auth } = await import('../lib/firebase');
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      console.error('Error signing up with email:', error);
      setError(error.message || 'Failed to sign up with email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  const signOut = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Dynamically import necessary function and auth object
      const { signOut: firebaseSignOut } = await import('firebase/auth');
      const { auth } = await import('../lib/firebase');

      await firebaseSignOut(auth);
      // User state will be updated by the onAuthStateChanged listener
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      signOut,
      isLoading,
      error
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}